import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { translate } from '@/data/fixture-transalations';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { getTeamImageById } from '@/helpers/images-helper';
import FixtureScore from '@/components/shared/fixture-score';
import { useEffect, useState } from 'react';
import SelectorDeLigasPorPartido from './SelectorDeLigasPorPartido';
import { LEAGUES } from '@/constants/leagues';
import JugadoresEventoPartido from './JugadoresEventoPartido';
import VerifiedIcon from '@/components/home/icons/verified-icon';
import FailIcon from '@/components/home/icons/fail-icon';
import ClockIcon from '@/components/shared/icons/clock-icon';
import JugadoresPartidoInjury from './JugadoresPartidoInjury';
import AnaliticaFantasyIcon from '@/components/shared/icons/analitica-fantasy-icon';
import AnaliticaScore from '@/components/shared/custom/analitica-score';

const JugadorEventosPorPartido = ({ player }) => {
    const theme = useTheme();
    const [fixtures, setFixtures] = useState(player?.p);
    const [ligas, setLigas] = useState(null);
    const [selectedLeague, setSelectedLeague] = useState(LEAGUES.LA_LIGA);
    const [selectedFixtures, setSelectedFixtures] = useState(fixtures);

    const accentColor = theme.palette.border?.accent3 || '#FF0080';

    const getAlphaColor = (color) => {
        try {
            if (color.startsWith('rgb(')) {
                return color.replace('rgb(', 'rgba(').replace(')', ', 0.25)');
            }
            return alpha(color, 0.25);
        } catch (error) {
            if (color.startsWith('#')) {
                return `${color}40`;
            }
            return color;
        }
    };

    useEffect(() => {
        setFixtures(player?.p);
        const uniqueLeagues = [...new Set(player.p.map((fixture) => fixture?.l).filter(Boolean))];
        setLigas(uniqueLeagues);
    }, [player]);

    useEffect(() => {
        if (selectedLeague === '-1') {
            setSelectedFixtures(fixtures);
        } else {
            setSelectedFixtures(fixtures.filter((fixture) => fixture?.l === selectedLeague));
        }
    }, [selectedLeague, fixtures]);

    if (selectedFixtures?.length === 0) {
        return null;
    }

    return (
        <Box display={'flex'} flexDirection={'column'} width={'100%'}>
            <Typography variant="h2" fontSize={16} mb={1}>
                Eventos Fantasy de todos los partidos de la temporada
            </Typography>
            <Box
                sx={{
                    position: 'relative',
                    border: 1,
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    width: '100%',
                    overflow: 'hidden',
                    '::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, ${accentColor} 0%, ${getAlphaColor(accentColor)} 60%, transparent 100%)`,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        pointerEvents: 'none',
                        zIndex: 1,
                    },
                }}
            >
                <SelectorDeLigasPorPartido
                    ligas={ligas}
                    selectedLeague={selectedLeague}
                    setSelectedLeague={setSelectedLeague}
                />
                <Box
                    width={'100%'}
                    display={'flex'}
                    height={'45px'}
                    borderBottom={`1px solid ${theme.palette.border.main}`}
                >
                    <Typography fontSize={14} display={'flex'} alignItems="center" justifyContent={'center'} width="100px">
                        Partido
                    </Typography>
                    <Typography
                        fontSize={14}
                        display={'flex'}
                        alignItems="center"
                        justifyContent={'center'}
                        width="calc(100% - 260px)"
                    >
                        Eventos
                    </Typography>
                    <Typography fontSize={14} display={'flex'} alignItems="center" justifyContent={'center'} width="40px">
                        Tit
                    </Typography>
                    <Typography fontSize={14} display={'flex'} alignItems="center" justifyContent={'center'} width="40px">
                        <ClockIcon iconWidth={20} iconHeight={20} />
                    </Typography>
                    <Typography display={'flex'} alignItems="center" justifyContent={'center'} width="40px">
                        <FantasyLaLigaIcon iconWidth={20} iconHeight={20} />
                    </Typography>
                    <Typography display={'flex'} alignItems="center" justifyContent={'center'} width="40px">
                        <AnaliticaFantasyIcon width={25} />
                    </Typography>
                </Box>
                {selectedFixtures
                    ?.sort((a, b) => new Date(b.dt) - new Date(a.dt))
                    ?.map((fixture, index) => {
                        return (
                            <JugadorEventosPartido key={index} partido={fixture} teamId={player.team?.id} />
                        );
                    })}
            </Box>
        </Box>
    );
};

export default JugadorEventosPorPartido;

const JugadorEventosPartido = ({ partido, teamId }) => {
    const league = partido?.l;
    const equipoLocal = partido.home;
    const equipoVisitante = partido.away;
    const theme = useTheme();

    const getFixtureResult = () => {
        const goalsEquipoJugador = equipoLocal?.id === teamId ? equipoLocal?.g : equipoVisitante?.g;
        const goalsRival = equipoLocal?.id === teamId ? equipoVisitante?.g : equipoLocal?.g;

        if (goalsEquipoJugador === null || goalsRival === null) {
            // Partido pendiente
            return alpha(theme.palette.divider, 0.3);
        } else if (goalsEquipoJugador === goalsRival) {
            // Empate - amarillo con transparencia
            return alpha('#ffc107', 0.15);
        } else if (goalsEquipoJugador > goalsRival) {
            // Victoria - verde con transparencia
            return alpha('#4caf50', 0.15);
        } else {
            // Derrota - rojo con transparencia
            return alpha('#f44336', 0.15);
        }
    };

    return (
        <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            width={'100%'}
            height={'50px'}
            borderBottom={`1px solid ${theme.palette.border.main}`}
        >
            <Box
                borderRight={`1px solid ${theme.palette.border.main}`}
                width={'50px'}
                display={'flex'}
                alignItems={'center'}
                flexDirection={'column'}
                backgroundColor={getFixtureResult()}
                pt={0.2}
            >
                <CustomImageComponent
                    src={getCompetitionImage(league, 15)}
                    alt="competición"
                    width={15}
                    height={15}
                />
                <Typography style={{ fontSize: '12px', color: '#0070f3', marginTop: '2px' }}>
                    {translate(partido?.rd, true)}
                </Typography>
            </Box>
            <Box
                borderRight={`1px solid ${theme.palette.border.main}`}
                width={'50px'}
                display={'flex'}
                alignItems={'center'}
                flexDirection={'column'}
            >
                <Box display={'flex'} gap={0.5} alignItems={'center'}>
                    <CustomImageComponent
                        src={getTeamImageById(equipoLocal?.id, 15)}
                        alt={equipoLocal?.nm}
                        width={15}
                        height={15}
                    />
                    <Typography fontSize={12}>{equipoLocal?.g}</Typography>
                </Box>

                <Box display={'flex'} gap={0.5} alignItems={'center'}>
                    <CustomImageComponent
                        src={getTeamImageById(equipoVisitante?.id, 15)}
                        alt={equipoVisitante?.nm}
                        width={15}
                        height={15}
                    />
                    <Typography fontSize={12}>{equipoVisitante?.g}</Typography>
                </Box>
            </Box>
            <Box width={'calc(100% - 260px)'}>
                <Box display={'flex'} gap={0.5} px={0.5} flexWrap={'wrap'} justifyContent={'center'}>
                    {partido?.e?.map((event, index) => (
                        <JugadoresEventoPartido
                            key={index}
                            detail={event.d}
                            type={event.ty}
                            entity={event.e}
                            minute={event.t}
                            isRemoved={event.is}
                            isTitular={partido.it}
                        />
                    ))}
                    {partido?.ire && <JugadoresPartidoInjury reason={partido.ire} type={partido.ity} />}
                </Box>
            </Box>
            <Box
                width={'40px'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                borderRight={`1px solid ${theme.palette.border.main}`}
                borderLeft={`1px solid ${theme.palette.border.main}`}
            >
                {partido.ity?.toLowerCase() === 'missing fixture' ? (
                    '-'
                ) : partido?.it ? (
                    <VerifiedIcon width={20} />
                ) : (
                    <FailIcon width={20} />
                )}
            </Box>
            <Box
                width={'40px'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                fontSize={14}
                color={theme.palette.text.primary}
                borderRight={`1px solid ${theme.palette.border.main}`}
            >
                {partido.ity?.toLowerCase() === 'missing fixture' ? '-' : partido?.m}
            </Box>
            <Box
                width={'40px'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                borderRight={`1px solid ${theme.palette.border.main}`}
            >
                {partido.ity?.toLowerCase() === 'missing fixture' || partido.l !== LEAGUES.LA_LIGA ? (
                    '-'
                ) : (
                    <FixtureScore score={partido.f} isAvailable={true} width={20} height={20} />
                )}
            </Box>
            <Box width={'40px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                {partido?.rt ? (
                    <AnaliticaScore
                        score={partido.rt}
                        fontSize="10px"
                        width="20px"
                        height="20px"
                        styles={{ minWidth: '20px' }}
                        pstyles={{ color: '#fff' }}
                    />
                ) : (
                    '-'
                )}
            </Box>
        </Box>
    );
};
